import { useEffect, useState } from "react";
import { Button, Dialog, Icon, Spinner } from "@omnabase/ui";
import { REPO_DEFAULT_PATHS, REPO_NOTHING_WORDS, repositoryPathProblem, repositoryPaths, repositoryPathsFromLocation, repositoryReadsWords } from "@omnabase/shared";
import { ApiError } from "../../lib/api";
import { useSession } from "../../shell/store";
import { useNavigate, useWs } from "../../lib/nav";
import { integrations, useIntegrations, type GithubRepository } from "../../lib/integrations";
import { kb, type KbSource, type Scope } from "../../lib/knowledge";
import { useCan } from "../../lib/access";

/** Connect a GitHub repository (M5 round four), ported from the prototype's RepositoryDialog: GitHub must be connected first (the dialog
 *  leads to Settings › Integrations); then a repository of any connected account and the paths it reads (the cleanup round, 2026-10-12: no
 *  switches, a repository always gives its docs for answers and its code for investigations). The GitHub accounts round (2026-10-13): the
 *  repositories of every account, under its login (`GET …/integrations/github/repositories`), a spinner while they load; one that is a source
 *  in this project is shown greyed, one that is a source elsewhere stays pickable and says where (one source per repository per project).
 *  Without GitHub, holders of workspace-level projectSettings get the way to connect it, everyone else the sentence naming who does (2026-10-05). */
export function RepositoryDialog({ scope, onClose, onAdded }: { scope: Scope; onClose: () => void; onAdded: () => void }) {
  const { slug } = useWs(); const { toast } = useSession();
  const admin = useCan()("projectSettings"); // Settings › Integrations is a workspace section: a per-project administrator cannot open it
  const conn = useIntegrations(slug);
  const accounts = conn.data?.github ?? [];
  const [list, setList] = useState<GithubRepository[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [repo, setRepo] = useState<string>("");
  const [paths, setPaths] = useState(REPO_DEFAULT_PATHS.join("\n"));
  const [busy, setBusy] = useState(false);
  const here = (r: GithubRepository) => sourceHere(r, scope);
  useEffect(() => {
    if (!accounts.length) return;
    let live = true;
    integrations.repositories(slug).then((r) => { if (!live) return; setList(r.repositories); const free = r.repositories.filter((x) => !here(x)); setRepo((cur) => cur || (free[0] ? repoId(free[0]) : "")); }, (e: Error) => { if (live) setError(e.message); });
    return () => { live = false; };
  }, [slug, accounts.map((a) => a.installationId).join(",")]); // eslint-disable-line react-hooks/exhaustive-deps
  if (conn.data && !accounts.length) return admin ? <InstallFirst onClose={onClose} /> : <AskAnAdministrator onClose={onClose} />;
  const free = (list ?? []).filter((r) => !here(r));
  const picked = free.find((r) => repoId(r) === repo);
  const connect = async () => {
    if (!picked) return;
    const problem = pathsProblem(paths); if (problem) { setError(problem); return; } // a pattern is refused before it is sent (polish round one)
    setBusy(true); setError(null);
    const chosen = repositoryPaths(paths);
    try { await kb.addRepository(slug, scope.id, { installationId: picked.installationId, owner: picked.owner, repo: picked.name, paths: chosen }); toast(connectedWords(picked.name, chosen), { kind: "success" }); onAdded(); }
    catch (e) { setError(problemOf(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog title="Connect a GitHub repository" intro="README, docs folder and changelog by default; re-read on every push, so answers follow the code." onClose={onClose}
      footer={<><span className="ob-dialog__foot-meta">{list ? error ?? "" : ""}</span><Button onClick={onClose}>Cancel</Button><Button variant="primary" disabled={!picked || busy} onClick={() => void connect()}>Connect repository</Button></>}>
      <span className="ob-dialog__label">Repository</span>
      {!list ? (error ? <p className="ob-dialog__hint">{error}</p> : <p className="ob-dialog__hint ob-dialog__loading"><Spinner /> Reading the repositories of your GitHub accounts…</p>)
        : free.length === 0 ? <p className="ob-dialog__hint">Every repository of your GitHub accounts is a source in this project already. Add more on GitHub, under the app's settings.</p> : (
          <RepositoryGroups accounts={accounts} list={list} scope={scope} picked={repo} onPick={setRepo} />
        )}
      <PathsField value={paths} onChange={setPaths} />
    </Dialog>
  );
}

/** the connect toast: what the new source reads, or that it serves investigations only (an emptied box, the composer-and-fixes round, 2026-11-03) */
const connectedWords = (name: string, paths: readonly string[]): string => (paths.length ? `${name} connected · reading ${repositoryReadsWords(paths)}` : `${name} connected · for investigations only`);

/** "Edit paths" from a repository row's menu (polish round one, 2026-10-16): the connect dialog's paths box, prefilled with the source's own
 *  list (empty for "nothing for drafts", 2026-11-03); saving replaces it, and the API re-reads the source at once, dropping the files the new
 *  list leaves out. */
export function EditPathsDialog({ scope, source, onClose, onSaved }: { scope: Scope; source: KbSource; onClose: () => void; onSaved: () => void }) {
  const { slug } = useWs(); const { toast } = useSession();
  const [paths, setPaths] = useState(() => repositoryPathsFromLocation(source.location).join("\n"));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const problem = pathsProblem(paths); if (problem) { setError(problem); return; }
    setBusy(true); setError(null);
    const chosen = repositoryPaths(paths);
    try { await kb.patchSource(slug, scope.id, source.id, { paths: chosen }); toast(chosen.length ? `${source.name} · re-reading ${repositoryReadsWords(chosen)}` : `${source.name} · ${REPO_NOTHING_WORDS}`); onSaved(); }
    catch (e) { setError(problemOf(e)); setBusy(false); }
  };
  return (
    <Dialog title={`Edit the paths of ${source.name}`} intro="What drafts may read from this repository. Saving re-reads it at once and drops the files the new list leaves out." onClose={onClose}
      footer={<><span className="ob-dialog__foot-meta">{error ?? ""}</span><Button onClick={onClose}>Cancel</Button><Button variant="primary" disabled={busy} onClick={() => void save()}>Save paths</Button></>}>
      <PathsField value={paths} onChange={setPaths} />
    </Dialog>
  );
}

/** the paths box both dialogs share: one path per line, "/" for the whole repository, empty for nothing (2026-11-03) */
function PathsField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return <>
    <label className="ob-dialog__label" htmlFor="repo-paths">Paths it reads, one per line</label>
    <textarea id="repo-paths" aria-describedby="repo-paths-help" className="ob-dialog__input" rows={3} value={value} onChange={(e) => onChange(e.target.value)} />
    <p id="repo-paths-help" className="ob-dialog__hint">Drafts read the listed folders, or "/" for the whole repository. Leave empty: drafts read nothing; code investigations always read everything.</p>
  </>;
}
/** the first line the API would refuse, in its words (the shared rule), or null */
const pathsProblem = (text: string): string | null => text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean).map(repositoryPathProblem).find((p) => p !== null) ?? null;
/** an API refusal in words: a schema refusal names its first issue (the pattern hint), anything else its message */
function problemOf(e: unknown): string {
  const issues = e instanceof ApiError && Array.isArray(e.details) ? (e.details as { message?: string }[]) : [];
  return issues.find((i) => i.message)?.message ?? (e as Error).message;
}

const repoId = (r: { owner: string; name: string }) => `${r.owner}/${r.name}`;
/** whether the repository is a source in this scope already (Shared is the scope without a project id) */
const sourceHere = (r: GithubRepository, scope: Scope) => r.sourceIn.some((p) => (scope.shared ? p.id === null : p.id === scope.id));

/** the repositories under each account's login: a source in this project greyed ("Already a source here"), one elsewhere says where */
function RepositoryGroups({ accounts, list, scope, picked, onPick }: { accounts: { installationId: string; login: string }[]; list: GithubRepository[]; scope: Scope; picked: string; onPick: (id: string) => void }) {
  return (
    <div className="ob-repo-groups">{accounts.map((acc) => { const repos = list.filter((r) => r.installationId === acc.installationId); return repos.length === 0 ? null : (
      <div key={acc.installationId} className="ob-repo-group">
        <span className="ob-repo-group__login">{acc.login}</span>
        <ul className="ob-checks" aria-label={`Repositories of ${acc.login}`}>{repos.map((r) => { const id = repoId(r); const isHere = sourceHere(r, scope); const elsewhere = usedElsewhere(r, scope); return (
          <li key={id}>
            <label><input type="radio" name="repo" value={id} disabled={isHere} checked={!isHere && picked === id} onChange={() => onPick(id)} /> {r.name}{r.private ? " · private" : ""}{r.version ? ` · ${r.version}` : " · no release yet"}</label>
            {isHere ? <span className="ob-checks__note">Already a source here</span> : elsewhere ? <span className="ob-checks__note">Also a source in {elsewhere}</span> : null}
          </li>
        ); })}</ul>
      </div>
    ); })}</div>
  );
}
/** the other projects where the repository is a source, in words ("Omnalingo", "A and B", "A and another project"); null for none */
function usedElsewhere(r: GithubRepository, scope: Scope): string | null {
  const names = r.sourceIn.filter((p) => (scope.shared ? p.id !== null : p.id !== scope.id)).map((p) => p.name);
  if (r.hiddenSources > 0) names.push(r.hiddenSources === 1 ? "another project" : `${r.hiddenSources} other projects`);
  if (!names.length) return null;
  return names.length === 1 ? names[0]! : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

const GITHUB_READS = <ul className="ob-consequences"><li><Icon name="check" size="sm" />Contents of the repositories you pick, read-only</li><li><Icon name="check" size="sm" />Pushes and releases, so the knowledge base follows the code</li></ul>;

/** no installation yet, for a holder of workspace-level projectSettings: the way to Settings › Integrations to install the App */
function InstallFirst({ onClose }: { onClose: () => void }) {
  const { ws } = useWs(); const navigate = useNavigate();
  return (
    <Dialog title="Connect a GitHub repository" intro="Install the OmnaBase app on GitHub first: it reads the repositories you pick and nothing else, and GitHub shows you exactly which before you agree." onClose={onClose}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" icon="link" onClick={() => { onClose(); navigate(ws("/settings/integrations?connect=github")); }}>Install on GitHub</Button></>}>
      {GITHUB_READS}
    </Dialog>
  );
}
/** no installation yet, for everyone else (a per-project administrator included): who installs it, and no link to a page they cannot open */
function AskAnAdministrator({ onClose }: { onClose: () => void }) {
  return (
    <Dialog title="Connect a GitHub repository" intro="An administrator connects GitHub under Settings › Integrations. Once the app is installed, you pick the repositories here." onClose={onClose}
      footer={<Button onClick={onClose}>Close</Button>}>
      {GITHUB_READS}
    </Dialog>
  );
}
